import React from "react";
import {CancelToken} from "axios";
import Api from "../../../core/services/api/api";
import useIsMounted from "../use-is-mounted";
import CacheService from "../../../core/services/cache/cache_service";
import {useAuthenticationState} from "../use-auth-state";
import LocalStorageService from "../../../core/services/cache/local_storage_service";

export interface OperatorInformationConfiguration {
    OperatorProfileImageBaseUrl?: string;
}

export interface OperatorInformation {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    profileImage?: string;
    profileImageFormat?: string;
}

export interface AugmentedOperatorInformation extends OperatorInformation {
    name: string;
    configuration: OperatorInformationConfiguration;
}

export interface OperatorInformationController {
    getOperatorInformation: (args?: GetOperatorInformationArgs, cancelToken?: CancelToken) => Promise<AugmentedOperatorInformation | null>;
    setCachedOperatorInformation: React.Dispatch<React.SetStateAction<OperatorInformation | null>>;
}

const OperatorInformationStateContext = React.createContext<AugmentedOperatorInformation | null>(null);
const OperatorInformationControllerContext = React.createContext<OperatorInformationController>({
    getOperatorInformation: async () => null,
    setCachedOperatorInformation: async () => undefined,
});


export interface OperatorInformationProviderProps {
    setLocalState?: React.Dispatch<React.SetStateAction<OperatorInformation | null>>;
}

export interface GetOperatorInformationArgs {
    customAuthToken?: string;
    logger?: unknown;
    disregardCache?: boolean;
    setGlobalState?: boolean;
    dispatchEvent?: boolean;
}

/**
 * Augments the operator information by injecting name and configuration for imageBaseUrl.
 * @param state
 */
const augmentOperatorInformation = (state: OperatorInformation | null): AugmentedOperatorInformation | null => {
    return !state
        ? null
        : ({
            ...state,
            name: [state.firstName, state.lastName].filter(Boolean).join(' '),
            configuration: {
                OperatorProfileImageBaseUrl: CacheService.operatorProfileImageBaseUrl,
            },
        })
}

const OperatorInformationProvider: React.FC<React.PropsWithChildren<OperatorInformationProviderProps>> = ({children, setLocalState}) => {
    const {loggedIn} = useAuthenticationState();
    const [_state, _setState] = React.useState<OperatorInformation | null>(null);
    const state = React.useMemo<AugmentedOperatorInformation | null>(() => augmentOperatorInformation(_state), [_state]);
    const stateRef = React.useRef<AugmentedOperatorInformation | null>(state);
    /** Dedupes concurrent default-auth operator-info fetches onto one in-flight request. */
    const inFlightRef = React.useRef<Promise<AugmentedOperatorInformation | null> | null>(null);
    /** True while this provider writes operatorId so the storage listener ignores the same-tab echo. */
    const isSettingOperatorIdRef = React.useRef(false);
    const isMounted = useIsMounted();

    /**
     * Sets the state of the Operator information.
     */
    const setState = React.useCallback((OperatorInformation: React.SetStateAction<OperatorInformation | null>) => {
        _setState(prev => {
            const newState = typeof OperatorInformation === "function" ? OperatorInformation(prev) : OperatorInformation;
            stateRef.current = augmentOperatorInformation(newState);
            return newState;
        });
    }, [])

    /**
     * Sets the state of the Operator information to the local state if it is provided.
     */
    React.useEffect(() => {
        if (setLocalState) {
            setLocalState(state);
        }
    }, [setLocalState, state])

    /**
     * Applies a successful operator-information API response to state / operator-id cache.
     */
    const applyOperatorInformationResponse = React.useCallback((
        response: Awaited<ReturnType<typeof Api.getCurrentOperatorInformation>>,
        {setGlobalState, dispatchEvent}: Pick<GetOperatorInformationArgs, 'setGlobalState' | 'dispatchEvent'>,
    ): AugmentedOperatorInformation | null => {
        if (!isMounted()) {
            return null;
        }
        const result = response?.data ?? null;
        if (response?.resultFlag && setGlobalState !== false) {
            setState(result);
        }
        if (response?.resultFlag && !!result?.id && dispatchEvent !== false) {
            if (CacheService.getOperatorId() !== result.id) {
                isSettingOperatorIdRef.current = true;
                try {
                    CacheService.setOperatorId(result.id);
                } finally {
                    isSettingOperatorIdRef.current = false;
                }
            }
        }
        return augmentOperatorInformation(result);
    }, [isMounted, setState]);

    /**
     * Fetches the current operator profile. Reuses cache and dedupes concurrent default-auth calls.
     * Custom-auth callers do not share the default in-flight promise; shared fetches ignore cancel tokens so
     * a consumer cancel cannot abort the provider-owned load.
     */
    const getOperatorInformation = React.useCallback<OperatorInformationController['getOperatorInformation']>(async ({
                                                                                                                         customAuthToken,
                                                                                                                         logger,
                                                                                                                         disregardCache,
                                                                                                                         setGlobalState,
                                                                                                                         dispatchEvent
                                                                                                                     } = {}, cancelToken) => {
        if (customAuthToken) {
            const response = await Api.getCurrentOperatorInformation(
                () => ({Authorization: customAuthToken}),
                cancelToken,
                true,
                logger,
            );
            return applyOperatorInformationResponse(response, {setGlobalState, dispatchEvent});
        }

        if (!disregardCache && !!stateRef.current) {
            return stateRef.current;
        }
        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        const request = (async () => {
            try {
                const response = await Api.getCurrentOperatorInformation(
                    null,
                    null,
                    false,
                    logger,
                );
                return applyOperatorInformationResponse(response, {setGlobalState, dispatchEvent});
            } finally {
                inFlightRef.current = null;
            }
        })();

        inFlightRef.current = request;
        return request;
    }, [applyOperatorInformationResponse])

    /**
     * Fetches the operator information when the provider is mounted / user becomes logged in.
     * Also refetches when another tab changes the operator id (ignores same-tab echo from setOperatorId).
     */
    React.useEffect(() => {
        if (!loggedIn)
            return;
        void getOperatorInformation();
        const storageListener = (e: StorageEvent) => {
            if (e.key !== LocalStorageService.keys.operatorId)
                return;
            if (isSettingOperatorIdRef.current)
                return;
            if (e.newValue === e.oldValue)
                return;
            void getOperatorInformation({disregardCache: true, dispatchEvent: false});
        }
        window.addEventListener('storage', storageListener);
        return () => {
            window.removeEventListener('storage', storageListener);
        }
    }, [getOperatorInformation, loggedIn]);


    const controller = React.useMemo<OperatorInformationController>(() => ({
        getOperatorInformation,
        setCachedOperatorInformation: setState,
    }), [getOperatorInformation, setState]);

    return (
        <>
            <OperatorInformationControllerContext.Provider value={controller}>
                <OperatorInformationStateContext.Provider value={state}>
                    {children}
                </OperatorInformationStateContext.Provider>
            </OperatorInformationControllerContext.Provider>
        </>
    );
}

export default OperatorInformationProvider;

export const useOperatorInformation = () => React.useContext(OperatorInformationStateContext);

export const useOperatorInformationController = () => React.useContext(OperatorInformationControllerContext);